import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, from, gql } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
/* eslint-disable-next-line import/extensions */
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { isAuthenticated, getAccessToken, getRefreshToken, getImpersonatedUser, setSession } from './auth';
import uploadFetch from './uploadFetch';

export const publicClient = new ApolloClient({
  link: createHttpLink({ uri: `${process.env.MANAGER_BASE_PATH}/public/graphql` }),
  cache: new InMemoryCache(),
});

const REFRESH_MUTATION = gql`
  mutation Refresh($token: String!) {
    refresh(token: $token) {
      accessToken
      expiresAt
      refreshToken
    }
  }
`;

const refresh = async () => {
  const token = getRefreshToken();

  if (token) {
    try {
      const { data } = await publicClient.mutate({
        mutation: REFRESH_MUTATION,
        variables: { token },
      });

      if (data?.refresh) {
        const { accessToken, expiresAt, refreshToken } = data.refresh;
        setSession(accessToken, expiresAt, refreshToken);
        return;
      }
    } catch (e) {
      console.error(e);
    }
  }
  throw new Error('Unauthorized');
};

const uploadLink = createUploadLink({
  uri: `${process.env.MANAGER_BASE_PATH}/graphql`,
  fetch: uploadFetch,
  headers: {
    'Apollo-Require-Preflight': 'true',
  },
});

const authLink = setContext(async (_, { headers }) => {
  const contextHeaders = { ...headers };

  if (!isAuthenticated()) {
    await refresh();
  }

  const accessToken = getAccessToken();
  if (accessToken) {
    contextHeaders.Authorization = `Bearer ${accessToken}`;
  }
  const impersonateUserId = getImpersonatedUser();
  if (impersonateUserId) {
    contextHeaders.Impersonate = impersonateUserId;
  }
  return { headers: contextHeaders };
});

// const refreshLink = onError(({ networkError }) => {
//   if (networkError.statusCode === 401) {
//     window.location.reload();
//   }
// });

// Always remove __typename from input variables
export const cleanTypenameLink = new ApolloLink((operation, forward) => {
  const omitTypename = (key, value) => (key === '__typename' ? undefined : value);

  if (operation.variables) {
    const s = JSON.stringify(operation.variables);
    if (s.includes('__typename')) {
      operation.variables = JSON.parse(s, omitTypename);
    }
  }

  return forward ? forward(operation) : null;
});

export const authenticatedClient = new ApolloClient({
  link: from([authLink, cleanTypenameLink, uploadLink]),
  cache: new InMemoryCache(),
});
